import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
dayjs.extend(duration);

type RawEntry = {
  id: number;
  type: string;
  time: string;
  timezone: string;
};

type ProcessedEntry = {
  id: number;
  type: string;
  time: string;
  accumulated: string;
  duration?: string;
};

type ReportData = {
  [date: string]: {
    hrs: number;
    entries: ProcessedEntry[];
  };
};

// Entries that start a working block (counted)
const workingStartTypes = ["ClockIn", "BreakIn", "LunchIn"];

export function transformEntriesToReport(entries: RawEntry[]): ReportData {
  const grouped: ReportData = {};

  const entriesByDay: Record<string, RawEntry[]> = {};
  entries.forEach((entry) => {
    const day = dayjs(entry.time).format("YYYY-MM-DD");
    if (!entriesByDay[day]) entriesByDay[day] = [];
    entriesByDay[day].push(entry);
  });

  for (const day in entriesByDay) {
    const rawEntries = entriesByDay[day].slice().sort(
      (a, b) => dayjs(a.time).valueOf() - dayjs(b.time).valueOf()
    );

    const processed: ProcessedEntry[] = [];
    let totalSeconds = 0;

    for (let i = 0; i < rawEntries.length; i++) {
      const current = rawEntries[i];
      const currentTime = dayjs(current.time);
      const formattedTime = currentTime.format("hh:mm A");

      const next = rawEntries[i + 1];
      let durationInSeconds = 0;
      let durationStr: string | undefined = undefined;

      if (next) {
        const nextTime = dayjs(next.time);
        durationInSeconds = nextTime.diff(currentTime, "second");
        durationStr = formatDuration(durationInSeconds);

        // ✅ Only count if this entry starts a working block
        if (workingStartTypes.includes(current.type)) {
          totalSeconds += durationInSeconds;
        }
      }

      processed.push({
        id: current.id,
        type: current.type,
        time: formattedTime,
        accumulated: (totalSeconds / 3600).toFixed(2) + "h",
        ...(next ? { duration: durationStr } : {}),
      });
    }

    grouped[day] = {
      hrs: totalSeconds / 3600,
      entries: processed,
    };
  }

  return grouped;
}

function formatDuration(seconds: number): string {
  const d = dayjs.duration(seconds, "seconds");
  const h = d.hours();
  const m = d.minutes();
  return `${h > 0 ? `${h}h ` : ""}${m}m`.trim();
}
